/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Breadcrumbs, Container, Link as BCLink, Box, CircularProgress } from "@mui/material";
import { NavigateNext } from "@mui/icons-material";
import axios from "../../axios";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import usePermissionGuard from "../../hooks/usePermissionGuard";

// ─── Constants ────────────────────────────────────────────────────────────────
// Status comes from the API as a string (model accessor converts numeric → string)
const STATUS = {
  Open:      { label: "Open",      color: "#3b82f6", bg: "#eff6ff", code: 12 },
  Partial:   { label: "Partial",   color: "#f59e0b", bg: "#fffbeb", code: 20 },
  Complete:  { label: "Complete",  color: "#22c55e", bg: "#f0fdf4", code: 21 },
  Cancelled: { label: "Cancelled", color: "#94a3b8", bg: "#f8fafc", code: 45 },
};

// Counts from backend use numeric keys; map them to string keys
const CODE_TO_KEY = { 12: "Open", 20: "Partial", 21: "Complete", 45: "Cancelled" };

const currencyFmt = new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" });
const dateFmt     = (d) => d ? new Date(d).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" }) : "—";

// ─── Debounce ─────────────────────────────────────────────────────────────────
function useDebounce(value, delay) {
  const [dv, setDv] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDv(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return dv;
}

// ─── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const s = STATUS[status] ?? { label: status, color: "#6b7280", bg: "#f9fafb" };
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 99,
      fontSize: 11, fontWeight: 600, letterSpacing: ".02em",
      color: s.color, background: s.bg, border: `1px solid ${s.color}30`,
      whiteSpace: "nowrap",
    }}>
      {s.label}
    </span>
  );
};

// ─── Filter tab ───────────────────────────────────────────────────────────────
const FilterTab = ({ label, count, value, active, onClick }) => (
  <button
    onClick={() => onClick(value)}
    style={{
      display: "flex", alignItems: "center", gap: 6,
      padding: "7px 14px", borderRadius: 7, border: "none",
      background: active ? "#6366f1" : "transparent",
      color: active ? "#fff" : "#64748b",
      fontWeight: active ? 600 : 500, fontSize: 13,
      cursor: "pointer", transition: "all .15s", whiteSpace: "nowrap",
    }}
  >
    {label}
    {count != null && (
      <span style={{
        fontSize: 11, fontWeight: 700,
        background: active ? "rgba(255,255,255,.25)" : "#f1f5f9",
        color: active ? "#fff" : "#64748b",
        borderRadius: 99, padding: "1px 7px", minWidth: 20, textAlign: "center",
      }}>
        {count}
      </span>
    )}
  </button>
);

// ─── Pagination ───────────────────────────────────────────────────────────────
const Pager = ({ data, onPageChange }) => {
  if (!data || data.last_page <= 1) return null;
  const { current_page, last_page, from, to, total } = data;

  const pages = [];
  for (let i = 1; i <= last_page; i++) {
    if (i === 1 || i === last_page || (i >= current_page - 2 && i <= current_page + 2)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }

  const btn = (label, target, disabled, active = false) => (
    <button
      key={`${label}-${target}`}
      disabled={disabled}
      onClick={() => !disabled && typeof target === "number" && onPageChange(target)}
      style={{
        minWidth: 32, height: 32, padding: "0 8px",
        border: active ? "1px solid #6366f1" : "1px solid #e2e8f0",
        borderRadius: 6, fontSize: 13, cursor: disabled ? "default" : "pointer",
        background: active ? "#6366f1" : "#fff",
        color: active ? "#fff" : disabled ? "#cbd5e1" : "#374151",
        fontWeight: active ? 600 : 400,
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "14px 20px", borderTop: "1px solid #f1f5f9", flexWrap: "wrap", gap: 8,
    }}>
      <span style={{ fontSize: 12, color: "#94a3b8" }}>
        {from}–{to} of {total?.toLocaleString()} orders
      </span>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {btn("‹", current_page - 1, current_page === 1)}
        {pages.map((p, i) =>
          p === "…"
            ? <span key={`e${i}`} style={{ padding: "0 4px", color: "#cbd5e1", lineHeight: "32px" }}>…</span>
            : btn(p, p, false, p === current_page)
        )}
        {btn("›", current_page + 1, current_page === last_page)}
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const StoreOrders = () => {
  usePermissionGuard("view_orders");
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [result, setResult]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [status, setStatus]     = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo]     = useState("");
  const [page, setPage]         = useState(1);

  const debouncedSearch = useDebounce(search, 400);
  const abortRef = useRef(null);

  const fetchOrders = useCallback((p = 1) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setLoading(true);

    const params = new URLSearchParams({
      page, search: debouncedSearch, status,
      date_from: dateFrom, date_to: dateTo,
    });
    params.set("page", p);

    axios
      .get(`/admin/store/dashboard/orders?${params}`, { signal: abortRef.current.signal })
      .then(r => { setResult(r.data); setLoading(false); })
      .catch(e => { if (e?.code !== "ERR_CANCELED") setLoading(false); });
  }, [debouncedSearch, status, dateFrom, dateTo]);

  useEffect(() => { setPage(1); fetchOrders(1); }, [debouncedSearch, status, dateFrom, dateTo]);

  const handlePage = (p) => { setPage(p); fetchOrders(p); };

  const clearFilters = () => { setSearch(""); setStatus("all"); setDateFrom(""); setDateTo(""); };
  const hasFilters = search || status !== "all" || dateFrom || dateTo;

  // counts keys are numeric from DB; map to string keys for display
  const rawCounts = result?.counts ?? {};
  const counts    = Object.fromEntries(
    Object.entries(rawCounts).map(([k, v]) => [CODE_TO_KEY[k] ?? k, v])
  );
  const orders = result?.data ?? null;
  const total  = Object.values(counts).reduce((s, v) => s + v, 0);

  // Build tab list: All + each status that has orders
  const tabs = [
    { value: "all", label: "All", count: total },
    ...Object.entries(STATUS)
      .filter(([key]) => counts[key] != null)
      .map(([key, s]) => ({ value: String(s.code), label: s.label, count: counts[key] })),
  ];

  return (
    <div>
      <Container sx={{ pb: "2rem" }}>

        {/* Breadcrumb */}
        <div role="presentation">
          <Breadcrumbs aria-label="breadcrumb" separator={<NavigateNext fontSize="small" />} sx={{ p: "14px 0" }}>
            <BCLink underline="none" color="inherit" href="/" fontSize="14px">{t("Home")}</BCLink>
            <BCLink underline="none" color="inherit" onClick={() => navigate("/store/dashboard")} sx={{ cursor: "pointer" }} fontSize="14px">Dashboard</BCLink>
            <BCLink underline="none" color="text.primary" aria-current="page" fontSize="14px">All Orders</BCLink>
          </Breadcrumbs>
        </div>

        {/* Page header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#0f172a" }}>
              {t("Orders")}
            </h1>
            {orders && (
              <span style={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>
                {orders.total?.toLocaleString()} {t("results")}
              </span>
            )}
          </Box>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>
            {t("All sales orders synced from ExactOnline.")}
          </p>
        </Box>

        {/* Filter card */}
        <div style={{
          background: "#fff", border: "1px solid #e2e8f0",
          borderRadius: 10, marginBottom: 20, overflow: "hidden",
        }}>
          {/* Status tabs */}
          <div style={{
            display: "flex", gap: 4, padding: "10px 12px",
            borderBottom: "1px solid #f1f5f9", flexWrap: "wrap",
          }}>
            {tabs.map(tab => (
              <FilterTab
                key={tab.value}
                value={tab.value}
                label={tab.label}
                count={tab.count}
                active={status === tab.value}
                onClick={setStatus}
              />
            ))}
          </div>

          {/* Search + date row */}
          <div style={{
            display: "flex", flexWrap: "wrap", gap: 10,
            padding: "12px 16px", alignItems: "center",
          }}>
            {/* Search */}
            <div style={{ position: "relative", flex: "1 1 220px", minWidth: 180 }}>
              <svg style={{
                position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
                width: 14, height: 14, color: "#94a3b8", pointerEvents: "none",
              }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t("Search by order number or reference…")}
                style={{
                  width: "100%", boxSizing: "border-box",
                  paddingLeft: 32, paddingRight: 12, paddingTop: 8, paddingBottom: 8,
                  border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13,
                  outline: "none", color: "#1e293b", background: "#f8fafc",
                }}
                onFocus={e => (e.target.style.borderColor = "#6366f1")}
                onBlur={e  => (e.target.style.borderColor = "#e2e8f0")}
              />
            </div>

            {/* Date from */}
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              title={t("From date")}
              style={{
                flex: "0 0 auto", padding: "8px 10px",
                border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13,
                color: dateFrom ? "#1e293b" : "#94a3b8", background: "#f8fafc", outline: "none",
              }}
            />
            <span style={{ color: "#cbd5e1", fontSize: 13, userSelect: "none" }}>→</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              title={t("To date")}
              style={{
                flex: "0 0 auto", padding: "8px 10px",
                border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13,
                color: dateTo ? "#1e293b" : "#94a3b8", background: "#f8fafc", outline: "none",
              }}
            />

            {hasFilters && (
              <button onClick={clearFilters} style={{
                flex: "0 0 auto", padding: "8px 14px",
                border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13,
                color: "#64748b", background: "#fff", cursor: "pointer", fontWeight: 500,
              }}>
                {t("Clear")}
              </button>
            )}
          </div>
        </div>

        {/* Table card */}
        <div style={{
          background: "#fff", border: "1px solid #e2e8f0",
          borderRadius: 10, overflow: "hidden",
        }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
              <CircularProgress sx={{ color: "#6366f1" }} size={32} />
            </div>
          ) : !orders || orders.data.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8", fontSize: 14 }}>
              {t("No orders found.")}
            </div>
          ) : (
            <>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                    {[
                      { label: t("Order"), style: {} },
                      { label: t("Customer"), style: {} },
                      { label: t("Order Date"), style: {} },
                      { label: t("Your Ref"), style: {} },
                      { label: t("Amount excl. VAT"), style: { textAlign: "right" } },
                      { label: t("Status"), style: { textAlign: "center" } },
                    ].map((h, i) => (
                      <th key={i} style={{
                        padding: "10px 20px", textAlign: "left",
                        fontSize: 11, fontWeight: 600, color: "#64748b",
                        textTransform: "uppercase", letterSpacing: ".05em",
                        whiteSpace: "nowrap", ...h.style,
                      }}>
                        {h.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.data.map(order => (
                    <tr
                      key={order.OrderID}
                      onClick={() => navigate(`/store/orders/${order.OrderID}`)}
                      style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer", transition: "background .1s" }}
                      onMouseEnter={e => (e.currentTarget.style.background = "#f8fafc")}
                      onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                    >
                      {/* Order number */}
                      <td style={{ padding: "13px 20px", whiteSpace: "nowrap" }}>
                        <span style={{ fontWeight: 600, color: "#6366f1", fontSize: 13 }}>
                          #{order.OrderNumber}
                        </span>
                      </td>

                      {/* Customer */}
                      <td style={{ padding: "13px 20px" }}>
                        <div style={{ fontWeight: 500, fontSize: 13, color: "#0f172a" }}>
                          {order.ordered_by_account?.Name || "—"}
                        </div>
                        {order.ordered_by_account?.Code && (
                          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                            {order.ordered_by_account.Code}
                          </div>
                        )}
                      </td>

                      {/* Order date */}
                      <td style={{ padding: "13px 20px", fontSize: 13, color: "#475569", whiteSpace: "nowrap" }}>
                        {dateFmt(order.OrderDate)}
                      </td>

                      {/* Your ref */}
                      <td style={{ padding: "13px 20px", fontSize: 13, color: "#475569", maxWidth: 200 }}>
                        <span style={{
                          display: "block", overflow: "hidden",
                          textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                          {order.YourRef || <span style={{ color: "#cbd5e1" }}>—</span>}
                        </span>
                      </td>

                      {/* Amount */}
                      <td style={{ padding: "13px 20px", textAlign: "right", fontWeight: 600, fontSize: 13, color: "#0f172a", whiteSpace: "nowrap" }}>
                        {order.AmountDC != null ? currencyFmt.format(order.AmountDC) : "—"}
                      </td>

                      {/* Status */}
                      <td style={{ padding: "13px 20px", textAlign: "center" }}>
                        <StatusBadge status={order.Status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Pager data={orders} onPageChange={handlePage} />
            </>
          )}
        </div>

      </Container>
    </div>
  );
};

export default React.memo(StoreOrders);
